import { useEffect, useState } from "react";
import { api, ApiError } from "../api";
import { tg } from "../telegram";
import { useT } from "../i18n";
import { useTelegramBackButton } from "../hooks/useTelegramBackButton";
import { useBandVars } from "../hooks/useNorms";
import type { AutoCatalog, EventCounts, ReportOut } from "../types";
import { PeriodSel, durationText, fmtD, fmtDT, fmtT, resolve, secToIso, stepFor, stepText } from "../utils/period";

// Row steps the user may pick (seconds). A pick is allowed while the table stays
// ≤ MAX_ROWS rows (per-second: one hour) — the server checks the same.
const STEP_CHOICES = [1, 10, 60, 300, 900, 3600];
const MAX_ROWS = 3600;
// Rough page count (checked on the stand: within a few %): ~6 fixed pages (key figures,
// charts, bearings and oil, all 43, deviations), one more for events, ~35 table rows per
// page and table part.
const pagesFor = (rows: number, parts: number, events: boolean) => 6 + (events ? 1 : 0) + parts * Math.ceil(rows / 35);
const fmtInt = (n: number) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, " ");

// «Отчёт PDF»: what the report will contain for the current unit + period, the
// row step, the options, then the build call. The result stays on screen with
// «open» / «send».
export default function ReportSheet({ unit, sel, catalog, onClose }: {
  unit: number; sel: PeriodSel; catalog: AutoCatalog | null; onClose: () => void;
}) {
  const { t } = useT();
  const bv = useBandVars();  // the corridor goes into «Отклонения: напряжение вне {lo}–{hi} кВ»
  useTelegramBackButton(onClose, [onClose]);
  const [send, setSend] = useState(true);
  const [full, setFull] = useState(true);
  const [events, setEvents] = useState(true);
  const [step, setStep] = useState<number | null>(null);   // null = automatic (≤ 120 rows)
  const [counts, setCounts] = useState<EventCounts | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [out, setOut] = useState<ReportOut | null>(null);
  const [sending, setSending] = useState(false);

  const r = resolve(sel, catalog?.data_from, catalog?.data_to);
  useEffect(() => {
    if (!r) return;
    const p = new URLSearchParams({ unit: String(unit), from: secToIso(r.from), to: secToIso(r.to + 1) });
    api.get<EventCounts>(`/api/events/counts?${p.toString()}`).then(setCounts).catch(() => setCounts(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unit, r?.from, r?.to]);
  if (!r) return null;
  const fromIso = secToIso(r.from), toIso = secToIso(r.to);
  const span = r.to - r.from + 1;
  const sameDay = fromIso.slice(0, 10) === toIso.slice(0, 10);
  const line = sameDay ? `${fmtD(fromIso)} ${fmtT(fromIso)} → ${fmtT(toIso)}` : `${fmtDT(fromIso)} → ${fmtDT(toIso)}`;

  const autoStep = stepFor(span);
  const eff = step ?? autoStep;
  const rows = Math.ceil(span / eff);
  const pages = pagesFor(rows, full ? 3 : 1, events);
  const pages1 = pagesFor(rows, 1, events);
  const big = rows > 600;
  const stepShort = (s: number) =>
    s < 60 ? `${s} ${t("period.s")}` : s < 3600 ? `${s / 60} ${t("auto.min_short")}` : `${s / 3600} ${t("period.h")}`;
  const chips = [
    { s: null as number | null, label: t("report.step_auto"), sub: stepText(autoStep, t), off: false },
    ...STEP_CHOICES.map(s => {
      const n = Math.ceil(span / s);
      return { s: s as number | null, label: stepShort(s), sub: t("report.rows_n", { n: fmtInt(n) }), off: n > MAX_ROWS };
    }),
  ];
  const evTotal = counts ? counts.trip + counts.stop + counts.start + counts.dip : 0;
  const evLine = !counts ? "" : evTotal === 0 ? t("report.ev_none")
    : t("report.ev_counts", { trip: counts.trip, stop: counts.stop, start: counts.start, dip: counts.dip });

  const build = async () => {
    setBusy(true); setErr(null); setOut(null);
    tg()?.HapticFeedback?.impactOccurred("medium");
    try {
      // Long periods and per-second hours take up to a few minutes server-side: a longer leash than the default 30 s.
      const res = await api.post<ReportOut>("/api/scada_v2/report",
        { unit, from_ts: fromIso, to_ts: toIso, full_tables: full, send, step_s: step, events }, big ? 300_000 : 120_000);
      setOut(res);
      tg()?.HapticFeedback?.notificationOccurred(res.sent || !send ? "success" : "warning");
    } catch (e) {
      setErr((e as ApiError).message);
      tg()?.HapticFeedback?.notificationOccurred("error");
    } finally {
      setBusy(false);
    }
  };
  const open = () => {
    if (!out) return;
    const url = `${window.location.origin}${out.url}`;
    const w = tg();
    if (w?.openLink) w.openLink(url); else window.open(url, "_blank");
  };
  const sendNow = async () => {
    if (!out) return;
    setSending(true); setErr(null);
    try {
      await api.post(`/api/scada_v2/report/${out.id}/send`);
      setOut({ ...out, sent: true, send_error: "" });
      tg()?.HapticFeedback?.notificationOccurred("success");
    } catch (e) {
      setErr((e as ApiError).message);
    } finally {
      setSending(false);
    }
  };
  const rowStyle: React.CSSProperties = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "9px 0", borderTop: "1px solid var(--border)", fontSize: 14, cursor: "pointer" };
  const box: React.CSSProperties = { width: 20, height: 20, accentColor: "#34c759", margin: 0, flexShrink: 0 };
  const toggle = (on: boolean, label: string, set: (v: boolean) => void) => (
    <label style={rowStyle}>
      <span>{label}</span>
      <input type="checkbox" checked={on} onChange={e => set(e.target.checked)} style={box} />
    </label>
  );
  const caps: React.CSSProperties = { fontSize: 11, color: "var(--hint)", textTransform: "uppercase", letterSpacing: ".06em", margin: "12px 0 6px" };
  const mb = out ? (out.bytes >= 1048576 ? `${(out.bytes / 1048576).toFixed(1)} MB` : `${Math.round(out.bytes / 1024)} KB`) : "";

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 200, display: "flex", alignItems: "flex-end" }} onClick={busy ? undefined : onClose}>
      <div style={{ background: "var(--bg)", width: "100%", maxHeight: "92vh", overflowY: "auto", borderRadius: "16px 16px 0 0", padding: 16 }} onClick={e => e.stopPropagation()}>
        <h2 style={{ marginTop: 0 }}>{t("report.pdf_title")}</h2>
        <div className="card" style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 16, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>G{unit} · {line}</div>
          <div style={{ fontSize: 12, color: "var(--hint)" }}>
            {durationText(span, t)} · {t("period.tables")} {stepText(eff, t)} · {t("report.rows_n", { n: fmtInt(rows) })} · ≈ {fmtInt(pages)} {t("report.pages")} · 43 {t("report.metrics")}
          </div>
        </div>

        <div style={caps}>{t("report.step")}</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {chips.map(c => {
            const active = c.s === step;
            return (
              <button key={String(c.s)} disabled={c.off || busy} className={"turbine-chip" + (active ? " active" : "")}
                onClick={() => { tg()?.HapticFeedback?.selectionChanged?.(); setStep(c.s); }}
                style={{ flexDirection: "column", alignItems: "flex-start", gap: 1, padding: "6px 11px", borderRadius: 12, minHeight: 44,
                  ...(c.off ? { opacity: 0.45, borderStyle: "dashed", borderColor: "var(--border)", cursor: "not-allowed" } : {}) }}>
                <span style={{ fontSize: 14, fontWeight: active ? 700 : 600, lineHeight: 1.15 }}>{c.label}</span>
                <span style={{ fontSize: 11, color: "var(--hint)", lineHeight: 1.15 }}>{c.sub}</span>
              </button>
            );
          })}
        </div>
        <div style={{ fontSize: 12, color: "var(--hint)", marginTop: 8 }}>{t("report.step_hint")}</div>
        {chips.some(c => c.off) && <div style={{ fontSize: 12, color: "var(--hint)", marginTop: 4 }}>{t("report.step_off")}</div>}
        {full && pages > 150 && (
          <div style={{ fontSize: 12.5, color: "var(--yellow-text, #8a5a00)", background: "var(--yellow, rgba(255,159,10,0.16))", borderRadius: 10, padding: "8px 10px", marginTop: 8 }}>
            {t("report.many", { n: fmtInt(pages1) })}
          </div>
        )}

        <div style={caps}>{t("report.includes")}</div>
        <ul style={{ margin: "0 0 8px", paddingLeft: 18, fontSize: 14, display: "grid", gap: 4 }}>
          {["i1", "i2", "i3", "i4", "i5", ...(events ? ["i6"] : [])].map(k => <li key={k}>{t("report." + k, bv)}</li>)}
        </ul>
        <div style={{ fontSize: 12, color: "var(--hint)", marginBottom: 6 }}>{t("report.no_analysis")}</div>
        <label style={rowStyle}>
          <span style={{ display: "flex", flexDirection: "column" }}>
            <span>{t("report.events")}</span>
            {evLine && <span style={{ fontSize: 12, color: "var(--hint)" }}>{evLine}</span>}
          </span>
          <input type="checkbox" checked={events} onChange={e => setEvents(e.target.checked)} style={box} />
        </label>
        {toggle(full, t("report.full"), setFull)}
        {toggle(send, t("report.send"), setSend)}
        {err && <div className="alert error" style={{ marginTop: 10 }}>{err}</div>}
        {!out && (
          <button className="btn pdf" style={{ marginTop: 12 }} disabled={busy} onClick={build}>
            {busy ? <><span className="spinner" /> {big ? t("report.making_big") : t("report.making")}</> : t("report.make")}
          </button>
        )}
        {out && (
          <div style={{ background: "rgba(52,199,89,0.14)", borderRadius: 10, padding: "10px 12px", fontSize: 14, marginTop: 10 }}>
            <b>{t("report.done")} · {out.pages} {t("report.pages")} · {mb}</b>
            <div style={{ fontSize: 12, color: "var(--hint)" }}>{out.filename} · {t("report.rows_step", { n: out.rows, step: stepText(out.step_s, t) })}</div>
            <div style={{ marginTop: 4 }}>
              {out.sent ? `✅ ${t("report.sent")}` : (send && out.send_error ? `⚠️ ${t("report.not_sent", { err: out.send_error })}` : "")}
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <button className="btn secondary" style={{ padding: "10px 12px" }} onClick={open}>{t("report.open")}</button>
              {!out.sent && (
                <button className="btn" style={{ padding: "10px 12px" }} disabled={sending} onClick={sendNow}>
                  {sending ? <span className="spinner" /> : t("report.send_now")}
                </button>
              )}
            </div>
          </div>
        )}
        <button className="btn secondary" style={{ marginTop: 10 }} onClick={onClose} disabled={busy}>{t("common.close")}</button>
      </div>
    </div>
  );
}
